import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getChildren, updateChild, updateChildPin, getAdaptations } from '../../services/api';

// ─── Brand tokens (parent interface) ──────────────────────────────────────────

const C = {
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceMuted: '#F8FAFC',
  text: '#1E293B',
  textMuted: '#64748B',
  textSubtle: '#94A3B8',
  accent: '#0066FF',
  accentDark: '#0052CC',
  accentDarker: '#003D99',
  accentSoft: '#EFF4FF',
  accentBorder: '#C7D7FE',
  border: '#E2E8F0',
  borderStrong: '#CBD5E1',
  green: '#10B981',
  greenDark: '#059669',
  greenSoft: '#ECFDF5',
  greenBorder: '#A7F3D0',
  greenText: '#065F46',
  amber: '#F59E0B',
  amberSoft: '#FEF3C7',
  amberBorder: '#FDE68A',
  amberText: '#92400E',
  red: '#EF4444',
  redSoft: '#FEF2F2',
  redBorder: '#FECACA',
  redText: '#B91C1C',
};

const HEADING_FONT = "'Syne', sans-serif";
const BODY_FONT = "'DM Sans', sans-serif";

// Curriculum subjects mirroring server/curriculum/ks*.json — keys must match.
const SUBJECTS = [
  { key: 'english',            label: 'English',            icon: '📖' },
  { key: 'english_language',   label: 'English Language',   icon: '📝' },
  { key: 'english_literature', label: 'English Literature', icon: '📚' },
  { key: 'maths',              label: 'Maths',              icon: '🔢' },
  { key: 'science',            label: 'Science',            icon: '🧪' },
  { key: 'biology',            label: 'Biology',            icon: '🌱' },
  { key: 'chemistry',          label: 'Chemistry',          icon: '⚗️' },
  { key: 'physics',            label: 'Physics',            icon: '⚛️' },
  { key: 'computing',          label: 'Computing',          icon: '💻' },
  { key: 'history',            label: 'History',            icon: '🏛️' },
  { key: 'geography',          label: 'Geography',          icon: '🌍' },
  { key: 'art',                label: 'Art',                icon: '🎨' },
  { key: 'music',              label: 'Music',              icon: '🎵' },
  { key: 'pe',                 label: 'PE',                 icon: '⚽' },
  { key: 'dt',                 label: 'Design & Tech',      icon: '🛠️' },
  { key: 're',                 label: 'RE',                 icon: '🕊️' },
  { key: 'citizenship',        label: 'Citizenship',        icon: '🤝' },
  { key: 'french',             label: 'French',             icon: '🇫🇷' },
  { key: 'spanish',            label: 'Spanish',            icon: '🇪🇸' },
];

const TIME_LIMIT_OPTIONS = [15, 30, 45, 60];
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => h);

// GCSE exam boards — shown only for Year 10/11 (effective year). Keys must
// match GCSE_EXAM_BOARD_SUBJECTS in server/services/curriculum.js. Combined
// Science is offered as its own row because many children sit Combined
// (two GCSEs) rather than Triple Science (three).
// 11+ test providers — keys must match ELEVEN_PLUS_PROVIDERS in
// server/services/curriculum.js. Only GL Assessment has practice content;
// the other two are offered so a parent can tell us their child sits
// something else, which is the whole point of asking.
const ELEVEN_PLUS_PROVIDER_OPTIONS = [
  { key: 'gl',    label: 'GL Assessment' },
  { key: 'fsce',  label: 'FSCE' },
  { key: 'quest', label: 'Quest Assessments' },
];

const EXAM_BOARD_OPTIONS = ['AQA', 'Edexcel', 'OCR'];
const EXAM_BOARD_SUBJECTS = [
  { key: 'maths',              label: 'Maths',              icon: '🔢' },
  { key: 'english_language',   label: 'English Language',   icon: '📝' },
  { key: 'english_literature', label: 'English Literature', icon: '📚' },
  { key: 'biology',            label: 'Biology',            icon: '🌱' },
  { key: 'chemistry',          label: 'Chemistry',          icon: '⚗️' },
  { key: 'physics',            label: 'Physics',            icon: '⚛️' },
  { key: 'combined_science',   label: 'Combined Science',   icon: '🧪' },
  { key: 'history',            label: 'History',            icon: '🏛️' },
  { key: 'geography',          label: 'Geography',          icon: '🌍' },
  { key: 'computing',          label: 'Computer Science',   icon: '💻' },
  { key: 'french',             label: 'French',             icon: '🇫🇷' },
  { key: 'spanish',            label: 'Spanish',            icon: '🇪🇸' },
];

// Curated en-GB-leaning shortlist. The Web Speech API exposes whatever the OS
// has installed, so the actual voice played falls back via resolveVoice() when
// the selection isn't on the child's device.
const VOICE_OPTIONS = [
  { value: 'Flo (English (United Kingdom))',   label: 'Flo (English UK)' },
  { value: 'Sandy (English (United Kingdom))', label: 'Sandy (English UK)' },
  { value: 'Daniel',                           label: 'Daniel' },
  { value: 'Reed (English (United Kingdom))',  label: 'Reed (English UK)' },
  { value: 'Google UK English Female',         label: 'Google UK English Female' },
  { value: 'Google UK English Male',           label: 'Google UK English Male' },
];

// ─── Brand mark ───────────────────────────────────────────────────────────────

function LanternBrand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8,
        background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDarker} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(0, 102, 255, 0.3)',
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <rect x="5" y="3" width="6" height="8" rx="1" stroke="#fff" strokeWidth="1.4"/>
          <path d="M6.5 3V2h3v1M5 11h6M7 13h2" stroke="#fff" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      </div>
      <span style={{
        fontFamily: HEADING_FONT, fontSize: 17, fontWeight: 700,
        color: C.text, letterSpacing: '-0.01em',
      }}>Lantern</span>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionCard({ title, description, children }) {
  return (
    <section style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      padding: '20px 22px',
      marginBottom: 16,
    }}>
      <h2 style={{
        margin: 0,
        fontFamily: HEADING_FONT,
        fontSize: 16,
        fontWeight: 700,
        color: C.text,
        letterSpacing: '-0.01em',
      }}>
        {title}
      </h2>
      {description && (
        <p style={{ margin: '4px 0 16px', fontSize: 13, color: C.textMuted, lineHeight: 1.5 }}>
          {description}
        </p>
      )}
      {!description && <div style={{ height: 12 }} />}
      {children}
    </section>
  );
}

function Toggle({ label, checked, onChange, disabled }) {
  return (
    <label style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: disabled ? 'not-allowed' : 'pointer',
      padding: '10px 0',
      fontFamily: BODY_FONT,
    }}>
      <span style={{ fontSize: 14, color: C.text, fontWeight: 500 }}>{label}</span>
      <span
        role="switch"
        aria-checked={checked}
        style={{
          width: 40,
          height: 22,
          borderRadius: 999,
          background: checked ? C.accent : C.border,
          position: 'relative',
          transition: 'background 160ms ease',
          flexShrink: 0,
        }}
      >
        <span style={{
          position: 'absolute',
          top: 2,
          left: checked ? 20 : 2,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 3px rgba(15,23,42,0.25)',
          transition: 'left 160ms ease',
        }} />
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
      />
    </label>
  );
}

function PinChangeModal({ childName, onClose, onSubmit }) {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!/^\d{4,6}$/.test(pin)) {
      return setError('PIN must be 4–6 digits.');
    }
    if (pin !== confirmPin) {
      return setError('The two PINs don\'t match.');
    }
    setSubmitting(true);
    try {
      await onSubmit(pin);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not change the PIN. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pin-modal-title"
      onClick={() => !submitting && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        zIndex: 100,
      }}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          padding: '24px 26px',
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 20px 50px rgba(15,23,42,0.18)',
          fontFamily: BODY_FONT,
        }}
      >
        <h2 id="pin-modal-title" style={{
          margin: '0 0 6px',
          fontFamily: HEADING_FONT,
          fontSize: 18,
          fontWeight: 700,
          color: C.text,
          letterSpacing: '-0.01em',
        }}>
          Change {childName}'s PIN
        </h2>
        <p style={{ margin: '0 0 18px', fontSize: 13, color: C.textMuted, lineHeight: 1.55 }}>
          Choose a new 4–6 digit PIN. Tell {childName} the new PIN — they'll use it next time they sign in.
        </p>

        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: C.text }}>
          New PIN
        </label>
        <input
          type="password"
          inputMode="numeric"
          autoFocus
          value={pin}
          onChange={(e) => { setError(null); setPin(e.target.value.replace(/\D/g, '').slice(0, 6)); }}
          placeholder="4–6 digits"
          maxLength={6}
          disabled={submitting}
          style={{
            width: '100%',
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: '12px 14px',
            fontFamily: BODY_FONT,
            fontSize: 15,
            color: C.text,
            outline: 'none',
            boxSizing: 'border-box',
            marginBottom: 14,
          }}
        />
        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: C.text }}>
          Confirm PIN
        </label>
        <input
          type="password"
          inputMode="numeric"
          value={confirmPin}
          onChange={(e) => { setError(null); setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6)); }}
          placeholder="Type it again"
          maxLength={6}
          disabled={submitting}
          style={{
            width: '100%',
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: '12px 14px',
            fontFamily: BODY_FONT,
            fontSize: 15,
            color: C.text,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />

        {error && (
          <div role="alert" style={{
            marginTop: 14,
            padding: '10px 14px',
            background: C.redSoft,
            border: `1px solid ${C.redBorder}`,
            borderRadius: 10,
            color: C.redText,
            fontSize: 13,
            fontWeight: 500,
          }}>
            {error}
          </div>
        )}

        <div style={{ marginTop: 22, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              fontFamily: BODY_FONT, fontSize: 14, fontWeight: 600,
              padding: '10px 16px', background: '#fff', color: C.text,
              border: `1px solid ${C.border}`, borderRadius: 8,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            style={{
              fontFamily: BODY_FONT, fontSize: 14, fontWeight: 600,
              padding: '10px 16px', background: C.accent, color: '#fff',
              border: 'none', borderRadius: 8,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Saving…' : 'Save new PIN'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ChildSettings() {
  const { childId } = useParams();
  const navigate = useNavigate();

  const [child, setChild] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedToast, setSavedToast] = useState(false);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  // The adaptations catalogue comes from the server so the checkboxes and the
  // API's enum cannot drift. If the fetch fails the section simply does not
  // render — a parent loses one optional control rather than the whole page.
  const [adaptationCatalogue, setAdaptationCatalogue] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getAdaptations()
      .then(r => { if (!cancelled) setAdaptationCatalogue(r.data.adaptations || []); })
      .catch(() => { /* Section hides itself; the rest of settings still works. */ });
    return () => { cancelled = true; };
  }, []);

  // Load child + customisation
  useEffect(() => {
    let cancelled = false;
    getChildren()
      .then(r => {
        if (cancelled) return;
        const found = r.data.find(c => c.id === childId);
        if (!found) {
          setLoadError('That child no longer exists.');
          return;
        }
        setChild(found);
        setForm({
          timeLimitMinutes: found.time_limit_minutes,
          activeHoursStart: found.active_hours_start,
          activeHoursEnd: found.active_hours_end,
          voiceInputEnabled: found.voice_input_enabled === 1,
          voiceOutputEnabled: found.voice_output_enabled === 1,
          voiceName: found.voice_name || null,
          subjectRestrictions: found.subjectRestrictions, // null = all allowed
          instructions: found.instructions || '',
          adaptations: found.adaptations || [],
          curriculumYearOverride: found.curriculum_year_override, // null = no override
          // GCSE exam-board preferences (Year 10/11). Object keyed by subject
          // with values 'AQA' | 'Edexcel' | 'OCR'; subjects with no
          // preference are omitted. null when nothing has ever been set.
          examBoardPreferences: found.examBoardPreferences || {},
          elevenPlusProvider: found.elevenPlusProvider || '',
          readingFont:  (found.accessibility && found.accessibility.readingFont)  || 'default',
          textScale:    (found.accessibility && found.accessibility.textScale)    || 100,
          lineSpacing:  (found.accessibility && found.accessibility.lineSpacing)  || 'normal',
          reduceMotion: Boolean(found.accessibility && found.accessibility.reduceMotion),
        });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 401) {
          navigate('/parent/login');
          return;
        }
        setLoadError('Could not load this child\'s settings. Please try again.');
      });
    return () => { cancelled = true; };
  }, [childId, navigate]);

  function setField(name, value) {
    setSaveError(null);
    setForm(f => ({ ...f, [name]: value }));
  }

  function toggleAdaptation(key) {
    setSaveError(null);
    setForm(f => ({
      ...f,
      adaptations: f.adaptations.includes(key)
        ? f.adaptations.filter(k => k !== key)
        : [...f.adaptations, key],
    }));
  }

  function toggleSubject(key) {
    setSaveError(null);
    setForm(f => {
      // null → currently no restriction (all allowed). Unticking starts the
      // allow-list with all subjects MINUS the one being unticked.
      const allKeys = SUBJECTS.map(s => s.key);
      const current = f.subjectRestrictions === null ? allKeys : f.subjectRestrictions;
      const next = current.includes(key)
        ? current.filter(k => k !== key)
        : [...current, key];
      // If next includes everything, treat as "no restriction" (null).
      const isAll = next.length === allKeys.length && allKeys.every(k => next.includes(k));
      return { ...f, subjectRestrictions: isAll ? null : next };
    });
  }

  async function handleSave() {
    if (!form || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Only include subjects with a real board pick — "Not sure" is
      // represented by absence, not by an empty string. An empty object
      // clears the column server-side, which matches the parent setting
      // every subject back to "Not sure".
      const cleanedPreferences = Object.fromEntries(
        Object.entries(form.examBoardPreferences || {}).filter(([, v]) => v)
      );
      const payload = {
        timeLimitMinutes: form.timeLimitMinutes,
        activeHoursStart: form.activeHoursStart,
        activeHoursEnd: form.activeHoursEnd,
        voiceInputEnabled: form.voiceInputEnabled,
        voiceOutputEnabled: form.voiceOutputEnabled,
        voiceName: form.voiceName,
        subjectRestrictions: form.subjectRestrictions,
        instructions: form.instructions.trim() === '' ? null : form.instructions,
        adaptations: form.adaptations,
        curriculumYearOverride: form.curriculumYearOverride,
        examBoardPreferences: cleanedPreferences,
        elevenPlusProvider: form.elevenPlusProvider || null,
        readingFont:  form.readingFont,
        textScale:    Number(form.textScale),
        lineSpacing:  form.lineSpacing,
        reduceMotion: Boolean(form.reduceMotion),
      };
      await updateChild(childId, payload);
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 2400);
    } catch (err) {
      setSaveError(err?.response?.data?.error || 'Could not save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handlePinChange(newPin) {
    await updateChildPin(childId, newPin);
  }

  if (loadError) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, fontFamily: BODY_FONT, color: C.text }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 20px' }}>
          <div style={{
            background: C.redSoft,
            border: `1px solid ${C.redBorder}`,
            borderRadius: 10,
            padding: '14px 18px',
            color: C.redText,
            fontSize: 14,
            fontWeight: 500,
          }}>
            {loadError}
          </div>
        </div>
      </div>
    );
  }

  if (!child || !form) {
    return (
      <div style={{
        minHeight: '100vh',
        background: C.bg,
        fontFamily: BODY_FONT,
        color: C.text,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          border: `3px solid ${C.border}`, borderTopColor: C.accent,
          animation: 'cs-spin 800ms linear infinite',
        }} />
        <style>{`@keyframes cs-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const yearGroup = child.year_group;
  const overrideActive = form.curriculumYearOverride !== null && form.curriculumYearOverride !== yearGroup;
  const charCount = form.instructions.length;
  // (0, 0) is the "Always available" sentinel — matches the server-side
  // short-circuit in routes/child.js. When this is true the start/end
  // dropdowns are hidden and the end-after-start validator stops firing.
  const isAlwaysAvailable = form.activeHoursStart === 0 && form.activeHoursEnd === 0;
  const timeWindowInvalid = !isAlwaysAvailable && form.activeHoursStart >= form.activeHoursEnd;

  function setAlwaysAvailable(next) {
    setSaveError(null);
    if (next) {
      // Toggle on: collapse to the always-available sentinel.
      setForm(f => ({ ...f, activeHoursStart: 0, activeHoursEnd: 0 }));
    } else {
      // Toggle off: restore the schema defaults (15:00–20:00) so the
      // dropdowns reappear with sensible values rather than 00:00–00:00.
      setForm(f => ({ ...f, activeHoursStart: 15, activeHoursEnd: 20 }));
    }
  }

  return (
    <>
      <style>{`
        @keyframes cs-toast-in { 0% { opacity:0; transform: translateY(-6px); } 100% { opacity:1; transform: translateY(0); } }
        .cs-back:hover { background: ${C.surfaceMuted}; border-color: ${C.borderStrong}; }
        .cs-input:focus { border-color: ${C.accent}; box-shadow: 0 0 0 4px ${C.accent}22; }
      `}</style>
      <div style={{
        minHeight: '100vh',
        background: C.bg,
        fontFamily: BODY_FONT,
        color: C.text,
      }}>
        {/* Top nav */}
        <div style={{
          borderBottom: `1px solid ${C.border}`,
          background: '#fff',
          position: 'sticky',
          top: 0,
          zIndex: 5,
        }}>
          <div style={{
            maxWidth: 980,
            margin: '0 auto',
            padding: '14px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <LanternBrand />
          </div>
        </div>

        <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 20px 56px' }}>

          {/* Back button */}
          <button
            type="button"
            className="cs-back"
            onClick={() => navigate('/parent/dashboard')}
            style={{
              fontFamily: BODY_FONT, fontSize: 13, fontWeight: 600,
              padding: '8px 14px', background: '#fff', color: C.text,
              border: `1px solid ${C.border}`, borderRadius: 8,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
              marginBottom: 20,
              transition: 'background 160ms ease, border-color 160ms ease',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M11 7H3M7 3l-4 4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to dashboard
          </button>

          <h1 style={{
            margin: '0 0 6px',
            fontFamily: HEADING_FONT,
            fontSize: 28,
            fontWeight: 700,
            color: C.text,
            letterSpacing: '-0.025em',
          }}>
            Settings — {child.display_name}
          </h1>
          <p style={{ margin: '0 0 24px', fontSize: 14, color: C.textMuted }}>
            @{child.username} · Year {yearGroup}
          </p>

          {/* Override banner */}
          {overrideActive && (
            <div style={{
              background: C.amberSoft,
              border: `1px solid ${C.amberBorder}`,
              borderLeft: `4px solid ${C.amber}`,
              borderRadius: 10,
              padding: '12px 16px',
              marginBottom: 16,
              fontSize: 14,
              color: C.amberText,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
            }}>
              <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1.3 }}>📚</span>
              <span>
                Curriculum override is active — Lantern is treating {child.display_name} as Year {form.curriculumYearOverride} for lesson content (real year group: {yearGroup}).
              </span>
            </div>
          )}

          {/* Time limit */}
          <SectionCard
            title="Session time limit"
            description="How long each session can last before Lantern ends it automatically."
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {TIME_LIMIT_OPTIONS.map(min => {
                const sel = form.timeLimitMinutes === min;
                return (
                  <button
                    key={min}
                    type="button"
                    onClick={() => setField('timeLimitMinutes', min)}
                    style={{
                      padding: '10px 18px',
                      borderRadius: 999,
                      border: `1.5px solid ${sel ? C.accent : C.border}`,
                      background: sel ? C.accentSoft : '#fff',
                      color: sel ? C.accentDark : C.text,
                      fontFamily: BODY_FONT,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'background 160ms ease, border-color 160ms ease',
                    }}
                  >
                    {min} minutes
                  </button>
                );
              })}
            </div>
          </SectionCard>

          {/* Active hours */}
          <SectionCard
            title="Active hours"
            description="Lantern will only respond between these hours (24-hour clock, UK time)."
          >
            {/*
              Plain <button> rather than the project's shared <Toggle> because
              we need a visible description underneath the label and a single
              unambiguous click target. The previous incarnation wrapped a
              controlled checkbox in a <label> and had its own onClick on the
              role="switch" span — that dual handler made the toggle appear
              unresponsive depending on browser. The button has no `disabled`
              prop on purpose: this control must always be interactive so the
              parent can switch back to a time-restricted window at will.
            */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 14,
              padding: '6px 0 14px',
              fontFamily: BODY_FONT,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                  Always available
                </div>
                <div style={{ fontSize: 13, color: C.textMuted, marginTop: 2 }}>
                  Remove time restrictions — Lantern is available at any time.
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={isAlwaysAvailable}
                aria-label="Always available"
                onClick={() => setAlwaysAvailable(!isAlwaysAvailable)}
                style={{
                  width: 40,
                  height: 22,
                  borderRadius: 999,
                  background: isAlwaysAvailable ? C.accent : C.border,
                  border: 'none',
                  padding: 0,
                  position: 'relative',
                  transition: 'background 160ms ease',
                  flexShrink: 0,
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                <span aria-hidden="true" style={{
                  position: 'absolute',
                  top: 2,
                  left: isAlwaysAvailable ? 20 : 2,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: '#fff',
                  boxShadow: '0 1px 3px rgba(15,23,42,0.25)',
                  transition: 'left 160ms ease',
                }} />
              </button>
            </div>

            {!isAlwaysAvailable && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 500, color: C.text }}>
                    Start
                    <select
                      value={form.activeHoursStart}
                      onChange={(e) => setField('activeHoursStart', parseInt(e.target.value, 10))}
                      className="cs-input"
                      style={{
                        border: `1px solid ${C.border}`, borderRadius: 10,
                        padding: '10px 12px', fontFamily: BODY_FONT, fontSize: 15,
                        color: C.text, background: '#fff', outline: 'none', minWidth: 110,
                        transition: 'border-color 160ms ease, box-shadow 160ms ease',
                      }}
                    >
                      {HOUR_OPTIONS.map(h => (
                        <option key={h} value={h}>{h.toString().padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </label>
                  <span style={{ color: C.textSubtle, fontWeight: 500, paddingTop: 22 }}>to</span>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 500, color: C.text }}>
                    End
                    <select
                      value={form.activeHoursEnd}
                      onChange={(e) => setField('activeHoursEnd', parseInt(e.target.value, 10))}
                      className="cs-input"
                      style={{
                        border: `1px solid ${C.border}`, borderRadius: 10,
                        padding: '10px 12px', fontFamily: BODY_FONT, fontSize: 15,
                        color: C.text, background: '#fff', outline: 'none', minWidth: 110,
                        transition: 'border-color 160ms ease, box-shadow 160ms ease',
                      }}
                    >
                      {HOUR_OPTIONS.map(h => (
                        <option key={h} value={h}>{h.toString().padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </label>
                </div>
                {timeWindowInvalid && (
                  <p style={{ margin: '10px 0 0', fontSize: 12, color: C.redText, fontWeight: 500 }}>
                    The end time must be later than the start time.
                  </p>
                )}
              </>
            )}
          </SectionCard>

          {/* Voice */}
          <SectionCard
            title="Voice features"
            description="Voice input lets your child speak their questions; voice output reads Lantern's replies aloud."
          >
            <Toggle
              label="Voice input — speak instead of typing"
              checked={form.voiceInputEnabled}
              onChange={(v) => setField('voiceInputEnabled', v)}
            />
            <div style={{ height: 1, background: C.border, margin: '4px 0' }} />
            <Toggle
              label="Voice output — read Lantern's replies aloud"
              checked={form.voiceOutputEnabled}
              onChange={(v) => setField('voiceOutputEnabled', v)}
            />

            {/* Voice picker — only relevant when voice output is on. */}
            {form.voiceOutputEnabled && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px dashed ${C.border}` }}>
                <label
                  htmlFor="cs-voice-name"
                  style={{
                    display: 'block',
                    fontFamily: BODY_FONT,
                    fontSize: 13,
                    fontWeight: 500,
                    color: C.text,
                    marginBottom: 6,
                  }}
                >
                  Lantern's voice
                </label>
                <select
                  id="cs-voice-name"
                  value={form.voiceName || ''}
                  onChange={(e) => setField('voiceName', e.target.value || null)}
                  className="cs-input"
                  style={{
                    border: `1px solid ${C.border}`, borderRadius: 10,
                    padding: '10px 12px', fontFamily: BODY_FONT, fontSize: 15,
                    color: C.text, background: '#fff', outline: 'none', minWidth: 280,
                    transition: 'border-color 160ms ease, box-shadow 160ms ease',
                  }}
                >
                  <option value="">Default (auto-select an English voice)</option>
                  {VOICE_OPTIONS.map(v => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
                <p style={{ margin: '6px 4px 0', fontSize: 12, color: C.textSubtle, lineHeight: 1.5 }}>
                  Voices vary by device. If your selection isn't installed, Lantern will fall back to a UK English voice.
                </p>
              </div>
            )}
          </SectionCard>

          {/* Subjects */}
          <SectionCard
            title="Subjects"
            description="Tick the subjects Lantern can help with. All ticked = no restriction."
          >
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 10,
            }}>
              {SUBJECTS.map(s => {
                const checked = form.subjectRestrictions === null
                  ? true
                  : form.subjectRestrictions.includes(s.key);
                return (
                  <label
                    key={s.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      border: `1px solid ${checked ? C.accentBorder : C.border}`,
                      background: checked ? C.accentSoft : '#fff',
                      borderRadius: 10,
                      cursor: 'pointer',
                      transition: 'background 160ms ease, border-color 160ms ease',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSubject(s.key)}
                      style={{ width: 16, height: 16, accentColor: C.accent, cursor: 'pointer' }}
                    />
                    <span aria-hidden="true" style={{ fontSize: 16 }}>{s.icon}</span>
                    <span style={{ fontSize: 14, fontWeight: 500, color: checked ? C.accentDark : C.text }}>
                      {s.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </SectionCard>

          {/* How your child learns best — structured adaptations.

              This section exists so a parent does NOT have to write "my child
              has dyslexia" to get an adapted experience. A diagnosis about an
              identified child is Article 9 special category data under UK
              GDPR; an instruction like "use short sentences" is not. Lantern
              only ever needed the second — the model cannot act on a diagnosis
              except by inferring the accommodation, and the parent is far
              better placed to make that judgement.

              So this comes FIRST, above the free-text box, and the copy says
              plainly that no reason is needed. The strongest protection for
              information is not to collect it. */}
          {adaptationCatalogue.length > 0 && (
            <SectionCard
              title="How your child learns best"
              description="Tick anything that helps. Lantern will work this way in every session."
            >
              <p style={{
                margin: '0 0 12px',
                fontSize: 13,
                lineHeight: 1.5,
                color: C.textSubtle,
              }}>
                You don&rsquo;t need to tell us why &mdash; just what helps. Lantern
                adapts quietly, and your child is never told that anything has
                been changed for them.
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 10,
              }}>
                {adaptationCatalogue.map(a => {
                  const checked = form.adaptations.includes(a.key);
                  return (
                    <label
                      key={a.key}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: '10px 12px',
                        border: `1px solid ${checked ? C.accentBorder : C.border}`,
                        background: checked ? C.accentSoft : '#fff',
                        borderRadius: 10,
                        cursor: 'pointer',
                        transition: 'background 160ms ease, border-color 160ms ease',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAdaptation(a.key)}
                        style={{
                          width: 16,
                          height: 16,
                          marginTop: 2,
                          accentColor: C.accent,
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      />
                      <span>
                        <span style={{
                          display: 'block',
                          fontSize: 14,
                          fontWeight: 500,
                          color: checked ? C.accentDark : C.text,
                        }}>
                          {a.label}
                        </span>
                        <span style={{
                          display: 'block',
                          marginTop: 2,
                          fontSize: 12,
                          lineHeight: 1.45,
                          color: C.textSubtle,
                        }}>
                          {a.hint}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </SectionCard>
          )}

          {/* Anything else — the free-text field.
              This USED to be the primary way a parent described their child's
              needs, and its placeholder asked for a diagnosis outright. The
              checkboxes above now cover the common cases without one, so this
              is a fallback for anything the list does not reach.

              The copy changed accordingly: it no longer suggests writing that
              a child is dyslexic, and it says plainly not to include a
              diagnosis. Parents should know what happens to what they write
              BEFORE they write it — the fair-processing expectation, and what
              the ICO Children's Code asks for. Deliberately plain rather than
              legalistic: it is read by a parent on a phone, not a DPO. */}
          <SectionCard
            title="Anything else"
            description="Optional. Anything about how your child works that the list above doesn't cover."
          >
            <p style={{
              margin: '0 0 12px',
              padding: '10px 12px',
              background: C.accentSoft,
              border: `1px solid ${C.accentBorder}`,
              borderRadius: 10,
              fontSize: 13,
              lineHeight: 1.5,
              color: C.text,
            }}>
              What you write here is sent to the AI at the start of every session,
              so it can adapt to your child. It is encrypted, stored securely, and
              never shown to other families.
              {' '}
              <strong>Please don&rsquo;t include medical details, diagnoses, or
              anyone&rsquo;s full name.</strong>
              {' '}
              Lantern doesn&rsquo;t need to know why something helps in order to do
              it &mdash; the boxes above cover most of it, and this is just for
              anything they miss.
            </p>
            <textarea
              value={form.instructions}
              onChange={(e) => setField('instructions', e.target.value.slice(0, 2000))}
              placeholder="For example: she's working towards a music exam in the summer, so please use musical examples where they fit."
              rows={5}
              className="cs-input"
              style={{
                width: '100%',
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                padding: '12px 14px',
                fontFamily: BODY_FONT,
                fontSize: 14,
                color: C.text,
                outline: 'none',
                resize: 'vertical',
                lineHeight: 1.5,
                boxSizing: 'border-box',
                transition: 'border-color 160ms ease, box-shadow 160ms ease',
              }}
            />
            <div style={{
              marginTop: 6,
              fontSize: 12,
              color: charCount > 1900 ? C.amberText : C.textSubtle,
              textAlign: 'right',
              fontFamily: BODY_FONT,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {charCount} / 2000
            </div>
          </SectionCard>

          {/* Curriculum override */}
          <SectionCard
            title="Curriculum year"
            description="Lantern uses your child's year group by default. You can advance them up to 3 years for more challenging content."
          >
            <select
              value={form.curriculumYearOverride === null ? yearGroup : form.curriculumYearOverride}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setField('curriculumYearOverride', val === yearGroup ? null : val);
              }}
              className="cs-input"
              style={{
                border: `1px solid ${C.border}`, borderRadius: 10,
                padding: '10px 12px', fontFamily: BODY_FONT, fontSize: 15,
                color: C.text, background: '#fff', outline: 'none', minWidth: 240,
                transition: 'border-color 160ms ease, box-shadow 160ms ease',
              }}
            >
              <option value={yearGroup}>Year {yearGroup} (current — default)</option>
              {[1, 2, 3].map(offset => {
                const y = yearGroup + offset;
                if (y > 13) return null;
                return <option key={y} value={y}>Year {y} (+{offset})</option>;
              })}
            </select>
          </SectionCard>

          {/* GCSE exam boards — Year 10/11 only (effective year, so override
              into Y10 also reveals this). Combined Science sits alongside
              the three separate sciences; if a child sits Combined they
              should set Combined Science and ignore the three Bio/Chem/Phys
              rows. */}
          <SectionCard
            title="Reading and accessibility"
            description="Adjust how Lantern looks for your child. These change the chat only — they do not change what Lantern teaches. Worth trying if your child finds reading on screen tiring, or is dyslexic."
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label htmlFor="reading-font" style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
                  Font
                </label>
                <select
                  id="reading-font"
                  value={form.readingFont || 'default'}
                  onChange={(e) => setForm((f) => ({ ...f, readingFont: e.target.value }))}
                  style={{ width: '100%', minHeight: 44, padding: '10px 12px', fontSize: 14, borderRadius: 10, border: `1px solid ${C.border}` }}
                >
                  <option value="default">Standard</option>
                  <option value="dyslexia">Easier to read (dyslexia-friendly)</option>
                </select>
              </div>

              <div>
                <label htmlFor="text-scale" style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
                  Text size
                </label>
                <select
                  id="text-scale"
                  value={form.textScale || 100}
                  onChange={(e) => setForm((f) => ({ ...f, textScale: Number(e.target.value) }))}
                  style={{ width: '100%', minHeight: 44, padding: '10px 12px', fontSize: 14, borderRadius: 10, border: `1px solid ${C.border}` }}
                >
                  <option value={100}>Normal</option>
                  <option value={125}>Large</option>
                  <option value={150}>Extra large</option>
                </select>
              </div>

              <div>
                <label htmlFor="line-spacing" style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
                  Line spacing
                </label>
                <select
                  id="line-spacing"
                  value={form.lineSpacing || 'normal'}
                  onChange={(e) => setForm((f) => ({ ...f, lineSpacing: e.target.value }))}
                  style={{ width: '100%', minHeight: 44, padding: '10px 12px', fontSize: 14, borderRadius: 10, border: `1px solid ${C.border}` }}
                >
                  <option value="normal">Normal</option>
                  <option value="relaxed">More space between lines</option>
                </select>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={Boolean(form.reduceMotion)}
                  onChange={(e) => setForm((f) => ({ ...f, reduceMotion: e.target.checked }))}
                  style={{ width: 18, height: 18 }}
                />
                <span>Reduce movement and animation</span>
              </label>
            </div>
          </SectionCard>

          {/* 11+ test provider — Years 4-6 only, matching
              ELEVEN_PLUS_YEAR_RANGE on the server. Asked because Lantern's
              11+ practice is built around GL Assessment and several areas
              have moved to other providers; without this, a child in a
              moving area is quietly coached for the wrong paper. */}
          {(() => {
            const effectiveYear = form.curriculumYearOverride || yearGroup;
            if (effectiveYear < 4 || effectiveYear > 6) return null;
            const current = form.elevenPlusProvider || '';
            const unsupported = current && current !== 'gl';
            return (
              <SectionCard
                title="11+ test provider"
                description="Which 11+ test does your child sit? Lantern's practice questions are built around GL Assessment. Several areas have moved to other providers — Gloucestershire and Trafford to FSCE, and Dorset, Slough and Latymer to Quest — so it is worth checking with the school. Leave on 'Not sure' and Lantern will ask your child to check with you."
              >
                <select
                  id="eleven-plus-provider"
                  aria-label="11+ test provider"
                  value={current}
                  onChange={(e) => setForm((f) => ({ ...f, elevenPlusProvider: e.target.value }))}
                  style={{
                    width: '100%',
                    minHeight: 44,
                    padding: '10px 12px',
                    fontSize: 14,
                    borderRadius: 10,
                    border: `1px solid ${current ? C.accentBorder : C.border}`,
                    background: current ? C.accentSoft : '#fff',
                    color: C.text,
                  }}
                >
                  <option value="">Not sure</option>
                  {ELEVEN_PLUS_PROVIDER_OPTIONS.map((p) => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>

                {unsupported && (
                  <p style={{ margin: '10px 0 0', fontSize: 13, lineHeight: 1.45, color: C.text }}>
                    Lantern does not have practice papers for this test. It will still
                    help with the underlying maths and reasoning, which carry across any
                    11+ paper, and it will tell your child honestly that it does not know
                    their paper&rsquo;s format — so please use materials for that test
                    alongside Lantern rather than instead of them.
                  </p>
                )}
              </SectionCard>
            );
          })()}

          {(() => {
            const effectiveYear = form.curriculumYearOverride || yearGroup;
            if (effectiveYear !== 10 && effectiveYear !== 11) return null;
            return (
              <SectionCard
                title="GCSE exam boards"
                description="Pick the board for each subject so Lantern uses the matching specification (command words, mark schemes, sample questions). Leave on 'Not sure' if you're unsure — Lantern will use generic guidance."
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {EXAM_BOARD_SUBJECTS.map(s => {
                    const current = (form.examBoardPreferences && form.examBoardPreferences[s.key]) || '';
                    const selectId = `exam-board-${s.key}`;
                    return (
                      <div
                        key={s.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          padding: '10px 12px',
                          border: `1px solid ${current ? C.accentBorder : C.border}`,
                          background: current ? C.accentSoft : '#fff',
                          borderRadius: 10,
                          transition: 'background 160ms ease, border-color 160ms ease',
                        }}
                      >
                        <label
                          htmlFor={selectId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            flex: 1,
                            cursor: 'pointer',
                          }}
                        >
                          <span aria-hidden="true" style={{ fontSize: 16 }}>{s.icon}</span>
                          <span style={{ fontSize: 14, fontWeight: 500, color: current ? C.accentDark : C.text }}>
                            {s.label}
                          </span>
                        </label>
                        <select
                          id={selectId}
                          value={current}
                          onChange={(e) => {
                            const value = e.target.value;
                            setForm(f => {
                              const next = { ...(f.examBoardPreferences || {}) };
                              if (value) {
                                next[s.key] = value;
                              } else {
                                delete next[s.key];
                              }
                              return { ...f, examBoardPreferences: next };
                            });
                            setSaveError(null);
                          }}
                          className="cs-input"
                          style={{
                            border: `1px solid ${C.border}`, borderRadius: 8,
                            padding: '6px 10px', fontFamily: BODY_FONT, fontSize: 14,
                            color: C.text, background: '#fff', outline: 'none',
                            minWidth: 130,
                            transition: 'border-color 160ms ease, box-shadow 160ms ease',
                          }}
                        >
                          <option value="">Not sure</option>
                          {EXAM_BOARD_OPTIONS.map(board => (
                            <option key={board} value={board}>{board}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
                <p style={{ margin: '12px 4px 0', fontSize: 12, color: C.textSubtle, lineHeight: 1.5 }}>
                  Combined Science covers Biology, Chemistry, and Physics as a single 2-GCSE route. If your child sits Combined Science, set the Combined Science row and leave the three individual sciences as "Not sure".
                </p>
              </SectionCard>
            );
          })()}

          {/* Change PIN */}
          <SectionCard
            title="Sign-in PIN"
            description="Reset your child's PIN if they've forgotten it or you want to change it."
          >
            <button
              type="button"
              onClick={() => setPinModalOpen(true)}
              style={{
                fontFamily: BODY_FONT, fontSize: 14, fontWeight: 600,
                padding: '10px 16px', background: '#fff', color: C.accent,
                border: `1px solid ${C.accentBorder}`, borderRadius: 8,
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <rect x="3" y="6" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M5 6V4a2 2 0 0 1 4 0v2" stroke="currentColor" strokeWidth="1.4"/>
              </svg>
              Change PIN
            </button>
          </SectionCard>

          {/* Save error */}
          {saveError && (
            <div role="alert" style={{
              marginTop: 8,
              padding: '12px 16px',
              background: C.redSoft,
              border: `1px solid ${C.redBorder}`,
              borderRadius: 10,
              color: C.redText,
              fontSize: 14,
              fontWeight: 500,
            }}>
              {saveError}
            </div>
          )}

          {/* Save button */}
          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button
              type="button"
              onClick={() => navigate('/parent/dashboard')}
              disabled={saving}
              style={{
                fontFamily: BODY_FONT, fontSize: 14, fontWeight: 600,
                padding: '12px 20px', background: '#fff', color: C.text,
                border: `1px solid ${C.border}`, borderRadius: 8,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || timeWindowInvalid}
              style={{
                fontFamily: BODY_FONT, fontSize: 14, fontWeight: 600,
                padding: '12px 22px', background: C.accent, color: '#fff',
                border: 'none', borderRadius: 8,
                cursor: (saving || timeWindowInvalid) ? 'not-allowed' : 'pointer',
                opacity: (saving || timeWindowInvalid) ? 0.6 : 1,
                display: 'inline-flex', alignItems: 'center', gap: 8,
                boxShadow: '0 1px 0 rgba(0,61,153,0.4) inset, 0 1px 2px rgba(0,102,255,0.25), 0 4px 12px rgba(0,102,255,0.18)',
              }}
            >
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </div>

        {/* Saved toast */}
        {savedToast && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed',
              top: 70,
              left: '50%',
              transform: 'translateX(-50%)',
              background: C.greenSoft,
              border: `1px solid ${C.greenBorder}`,
              borderRadius: 10,
              padding: '10px 16px',
              color: C.greenText,
              fontSize: 14,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
              animation: 'cs-toast-in 200ms ease both',
              zIndex: 50,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 7l3 3 5-6" stroke={C.greenDark} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
            Settings saved
          </div>
        )}

        {/* PIN change modal */}
        {pinModalOpen && (
          <PinChangeModal
            childName={child.display_name}
            onClose={() => setPinModalOpen(false)}
            onSubmit={handlePinChange}
          />
        )}
      </div>
    </>
  );
}
